import React, { useState, useEffect } from 'react';
import { saveNickname, FIVE_LEAGUE, FL_GAME_MODE, CHAIN_SETTINGS } from '../shared/fiveLeague';
import { createRoom, findRoomByCode, joinRoom, quickMatch } from '../shared/roomTx';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import AdBanner from '../components/AdBanner';
import HowToPlayModal, { hasSeenTutorial } from '../components/HowToPlayModal';
import AuthModal from '../components/AuthModal';
import QuestionSubmitModal from '../components/QuestionSubmitModal';
import { auth, isEmailUser } from '../firebase';
import './Home.css';

// 機能フラグ: アンサーチェーンモードを有効にするか
const ENABLE_CHAIN_MODE = false;

function Home({ navigate, playerId, nickname, setNickname }) {
  const [mode, setMode] = useState('menu'); // menu, select-mode, create, join, quick
  const [inputNickname, setInputNickname] = useState(nickname);
  const [roomCode, setRoomCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showSubmitQuestion, setShowSubmitQuestion] = useState(false);
  const [, forceUpdate] = useState(0);

  // ゲームモード選択
  const [selectedGameMode, setSelectedGameMode] = useState(FL_GAME_MODE.ONEWORD);
  const [selectedAnswerCount, setSelectedAnswerCount] = useState(CHAIN_SETTINGS.ANSWER_COUNT_5);

  // 初回訪問時に遊び方を表示（登録済みユーザーは除く）
  useEffect(() => {
    if (!hasSeenTutorial() && !isEmailUser()) {
      setShowHowToPlay(true);
    }
  }, []);

  // 認証状態変更時に再レンダリング
  const handleAuthChange = () => {
    forceUpdate(n => n + 1);
  };

  const handleQuickMatch = async () => {
    if (!inputNickname.trim()) {
      setError('ニックネームを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      saveNickname(inputNickname.trim());
      setNickname(inputNickname.trim());

      const result = await quickMatch({
        playerId,
        nickname: inputNickname.trim(),
      });

      if (result.ok) {
        navigate('wait', { roomId: result.roomId });
      } else {
        setError('マッチングに失敗しました');
      }
    } catch (e) {
      setError('エラーが発生しました');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!inputNickname.trim()) {
      setError('ニックネームを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      saveNickname(inputNickname.trim());
      setNickname(inputNickname.trim());

      const config = selectedGameMode === FL_GAME_MODE.CHAIN
        ? { answerCount: selectedAnswerCount }
        : {};

      const result = await createRoom({
        hostId: playerId,
        nickname: inputNickname.trim(),
        gameMode: selectedGameMode,
        config,
      });

      if (result.ok) {
        navigate('wait', { roomId: result.roomId });
      } else {
        setError('ルームの作成に失敗しました');
      }
    } catch (e) {
      setError('エラーが発生しました');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRoom = async () => {
    if (!inputNickname.trim()) {
      setError('ニックネームを入力してください');
      return;
    }
    if (!roomCode.trim() || roomCode.length !== 6) {
      setError('6文字のルームコードを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      saveNickname(inputNickname.trim());
      setNickname(inputNickname.trim());

      // ルーム検索
      const findResult = await findRoomByCode(roomCode.toUpperCase());
      if (!findResult.ok) {
        if (findResult.reason === 'not-found') {
          setError('ルームが見つかりません');
        } else {
          setError('ルームの検索に失敗しました');
        }
        setLoading(false);
        return;
      }

      // ルーム参加
      const joinResult = await joinRoom(findResult.roomId, {
        playerId,
        nickname: inputNickname.trim(),
      });

      if (joinResult.ok) {
        navigate('wait', { roomId: findResult.roomId });
      } else {
        if (joinResult.reason === 'room-full') {
          setError('ルームが満員です');
        } else if (joinResult.reason === 'game-started') {
          setError('ゲームは既に開始されています');
        } else {
          setError('ルームへの参加に失敗しました');
        }
      }
    } catch (e) {
      setError('エラーが発生しました');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // メニュー画面
  if (mode === 'menu') {
    return (
      <div className="home-container animate-fade-in">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <div className="home-header">
            <h1 className="home-title">
              <NeonText color="pink" size="xl">QUIZ</NeonText>
              <NeonText color="accent" size="xl" style={{ whiteSpace: 'nowrap' }}>ONE WORD</NeonText>
            </h1>
            <p className="home-subtitle">
              <NeonText color="blue" size="sm">クイズ <span style={{ whiteSpace: 'nowrap' }}>ワン ワード</span></NeonText>
            </p>
          </div>

          <GlowingBorder color="purple" className="home-description-box">
            <div className="home-description">
              <p>{FIVE_LEAGUE.MIN_PLAYERS}〜{FIVE_LEAGUE.MAX_PLAYERS}人で協力して、</p>
              <p>文字を揃えて答えを完成させよう！</p>
            </div>
          </GlowingBorder>

          <div className="home-buttons">
            <CyberButton
              variant="accent"
              className="home-btn quick-btn glow-rotate"
              onClick={() => setMode('quick')}
            >
              ⚡ フリー対戦
            </CyberButton>
            <CyberButton
              variant="primary"
              className="home-btn glow-rotate"
              onClick={() => setMode(ENABLE_CHAIN_MODE ? 'select-mode' : 'create')}
            >
              ルームを作る
            </CyberButton>
            <button
              className="cyber-btn-secondary home-btn"
              onClick={() => setMode('join')}
            >
              ルームに参加
            </button>
            <button
              className="cyber-btn-secondary home-btn how-to-play-btn"
              onClick={() => setShowHowToPlay(true)}
            >
              ? 遊び方
            </button>
            <button
              className="cyber-btn-secondary home-btn account-btn"
              onClick={() => setShowAuth(true)}
            >
              {isEmailUser() ? `👤 ${auth.currentUser?.email?.split('@')[0]}` : '👤 アカウント'}
            </button>
            <button
              className="cyber-btn-secondary home-btn submit-question-btn"
              onClick={() => setShowSubmitQuestion(true)}
            >
              ✏️ 問題を投稿
            </button>
          </div>

          <HowToPlayModal
            isOpen={showHowToPlay}
            onClose={() => setShowHowToPlay(false)}
          />

          <AuthModal
            isOpen={showAuth}
            onClose={() => setShowAuth(false)}
            onAuthChange={handleAuthChange}
          />

          <QuestionSubmitModal
            isOpen={showSubmitQuestion}
            onClose={() => setShowSubmitQuestion(false)}
          />

          <div className="home-footer">
            <AdBanner slot="HOME_BANNER" format="horizontal" />
            <p className="footer-text">みんなで1文字ずつ答える協力クイズ</p>
            <div className="footer-links">
              <button className="footer-link" onClick={() => navigate('privacy')}>
                プライバシーポリシー
              </button>
              <span className="footer-divider">|</span>
              <button className="footer-link" onClick={() => navigate('terms')}>
                利用規約
              </button>
              <span className="footer-divider">|</span>
              <button className="footer-link" onClick={() => navigate('legal')}>
                特商法表記
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ゲームモード選択画面
  if (mode === 'select-mode') {
    return (
      <div className="home-container animate-slide-up">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('menu')}>
            ← 戻る
          </button>

          <h2 className="form-title">
            <NeonText color="pink" size="lg">ゲームを選ぶ</NeonText>
          </h2>

          <div className="mode-selection">
            {/* ワンワードモード */}
            <GlowingBorder
              color={selectedGameMode === FL_GAME_MODE.ONEWORD ? 'accent' : 'blue'}
              className={`mode-card ${selectedGameMode === FL_GAME_MODE.ONEWORD ? 'selected' : ''}`}
              onClick={() => setSelectedGameMode(FL_GAME_MODE.ONEWORD)}
            >
              <div className="mode-card-content">
                <h3 className="mode-title">🎯 ワンワード</h3>
                <p className="mode-desc">全員で1文字ずつ答えを完成させる協力クイズ</p>
                <ul className="mode-features">
                  <li>✓ {FIVE_LEAGUE.MIN_PLAYERS}〜{FIVE_LEAGUE.MAX_PLAYERS}人で協力</li>
                  <li>✓ 全員同時に回答</li>
                  <li>✓ 最大10問に挑戦</li>
                </ul>
              </div>
            </GlowingBorder>

            {/* チェーンモード（フラグがONの時のみ表示） */}
            {ENABLE_CHAIN_MODE && (
              <GlowingBorder
                color={selectedGameMode === FL_GAME_MODE.CHAIN ? 'accent' : 'blue'}
                className={`mode-card ${selectedGameMode === FL_GAME_MODE.CHAIN ? 'selected' : ''}`}
                onClick={() => setSelectedGameMode(FL_GAME_MODE.CHAIN)}
              >
                <div className="mode-card-content">
                  <h3 className="mode-title">⚡ アンサーチェーン</h3>
                  <p className="mode-desc">順番に答えて全問正解を目指す早押しクイズ</p>
                  <ul className="mode-features">
                    <li>✓ {FIVE_LEAGUE.MIN_PLAYERS}〜{FIVE_LEAGUE.MAX_PLAYERS}人で対戦</li>
                    <li>✓ 順番にターン制</li>
                    <li>✓ 時間切れで終了</li>
                  </ul>
                </div>
              </GlowingBorder>
            )}
          </div>

          {/* チェーンモード選択時の追加オプション */}
          {selectedGameMode === FL_GAME_MODE.CHAIN && (
            <GlowingBorder color="purple" className="chain-options">
              <div className="chain-option-content">
                <label className="option-label">回答数を選択</label>
                <div className="answer-count-buttons">
                  <button
                    className={`answer-count-btn ${selectedAnswerCount === 5 ? 'selected' : ''}`}
                    onClick={() => setSelectedAnswerCount(5)}
                  >
                    5問<br /><span className="time-label">30秒</span>
                  </button>
                  <button
                    className={`answer-count-btn ${selectedAnswerCount === 10 ? 'selected' : ''}`}
                    onClick={() => setSelectedAnswerCount(10)}
                  >
                    10問<br /><span className="time-label">60秒</span>
                  </button>
                </div>
              </div>
            </GlowingBorder>
          )}

          <CyberButton
            variant="primary"
            className="submit-btn"
            onClick={() => setMode('create')}
          >
            次へ →
          </CyberButton>
        </div>
      </div>
    );
  }

  // ルーム作成画面
  if (mode === 'create') {
    return (
      <div className="home-container animate-slide-up">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('menu')}>
            ← 戻る
          </button>

          <h2 className="form-title">
            <NeonText color="pink" size="lg">ルームを作る</NeonText>
          </h2>

          <GlowingBorder color="blue" className="form-box">
            <div className="form-group">
              <label className="form-label">ニックネーム</label>
              <input
                type="text"
                className="form-input cyber-input"
                value={inputNickname}
                onChange={(e) => setInputNickname(e.target.value)}
                placeholder="例: たろう"
                maxLength={FIVE_LEAGUE.NICKNAME_MAX_LENGTH}
                disabled={loading}
              />
            </div>
          </GlowingBorder>

          {error && <p className="error-message">{error}</p>}

          <CyberButton
            variant="primary"
            className="submit-btn"
            onClick={handleCreateRoom}
            disabled={loading}
          >
            {loading ? '作成中...' : 'ルームを作成'}
          </CyberButton>
        </div>
      </div>
    );
  }

  // ルーム参加画面
  if (mode === 'join') {
    return (
      <div className="home-container animate-slide-up">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('menu')}>
            ← 戻る
          </button>

          <h2 className="form-title">
            <NeonText color="accent" size="lg">ルームに参加</NeonText>
          </h2>

          <GlowingBorder color="blue" className="form-box">
            <div className="form-group">
              <label className="form-label">ニックネーム</label>
              <input
                type="text"
                className="form-input cyber-input"
                value={inputNickname}
                onChange={(e) => setInputNickname(e.target.value)}
                placeholder="例: たろう"
                maxLength={FIVE_LEAGUE.NICKNAME_MAX_LENGTH}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">ルームコード</label>
              <input
                type="text"
                className="form-input cyber-input room-code-input"
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="ABC123"
                maxLength={6}
                disabled={loading}
              />
            </div>
          </GlowingBorder>

          {error && <p className="error-message">{error}</p>}

          <CyberButton
            variant="primary"
            className="submit-btn"
            onClick={handleJoinRoom}
            disabled={loading}
          >
            {loading ? '参加中...' : 'ルームに参加'}
          </CyberButton>
        </div>
      </div>
    );
  }

  // フリー対戦（クイックマッチ）画面
  if (mode === 'quick') {
    return (
      <div className="home-container animate-slide-up">
        <CyberBackground showParticles={true} />

        <div className="home-content">
          <button className="back-btn" onClick={() => setMode('menu')}>
            ← 戻る
          </button>

          <h2 className="form-title">
            <NeonText color="accent" size="lg">⚡ フリー対戦</NeonText>
          </h2>
          <p className="quick-desc">空いているルームに自動で参加、なければ新規作成します</p>

          <GlowingBorder color="accent" className="form-box">
            <div className="form-group">
              <label className="form-label">ニックネーム</label>
              <input
                type="text"
                className="form-input cyber-input"
                value={inputNickname}
                onChange={(e) => setInputNickname(e.target.value)}
                placeholder="例: たろう"
                maxLength={FIVE_LEAGUE.NICKNAME_MAX_LENGTH}
                disabled={loading}
              />
            </div>
          </GlowingBorder>

          {error && <p className="error-message">{error}</p>}

          <CyberButton
            variant="accent"
            className="submit-btn"
            onClick={handleQuickMatch}
            disabled={loading}
          >
            {loading ? 'マッチング中...' : '⚡ マッチング開始'}
          </CyberButton>
        </div>
      </div>
    );
  }

  return null;
}

export default Home;
